import { useState, FormEvent } from 'react';

export default function Registration() {
  const [selectedParts, setSelectedParts] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    referrer: '',
    nickname: '',
    gender: '',
    birthDate: '',
    birthTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const partOptions = [
    { id: 'part1', label: '1部のみ（自分と相手の取扱説明書）', price: '¥4,000' },
    { id: 'part2', label: '2部のみ（ビジョンマップ）', price: '¥4,000' },
    { id: 'part1and2', label: '1部+2部セット', price: '¥5,000', badge: 'お得！' }
  ];

  const togglePart = (partId: string) => {
    // 1部+2部セットを選択した場合、1部のみと2部のみを除外
    if (partId === 'part1and2') {
      const filtered = selectedParts.filter(p => p !== 'part1' && p !== 'part2');
      if (selectedParts.includes('part1and2')) {
        setSelectedParts(filtered.filter(p => p !== 'part1and2'));
      } else {
        setSelectedParts([...filtered, 'part1and2']);
      }
    }
    // 1部のみまたは2部のみを選択した場合、1部+2部セットを除外
    else if (partId === 'part1' || partId === 'part2') {
      const filtered = selectedParts.filter(p => p !== 'part1and2');
      if (selectedParts.includes(partId)) {
        setSelectedParts(filtered.filter(p => p !== partId));
      } else {
        setSelectedParts([...filtered, partId]);
      }
    }
    // 懇親会は独立して選択可能
    else {
      if (selectedParts.includes(partId)) {
        setSelectedParts(selectedParts.filter(p => p !== partId));
      } else {
        setSelectedParts([...selectedParts, partId]);
      }
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('referrer', formData.referrer);
      formDataToSend.append('selectedParts', selectedParts.join(', '));
      
      // 1部を含む場合のみ診断情報を送信
      if (selectedParts.includes('part1') || selectedParts.includes('part1and2')) {
        formDataToSend.append('nickname', formData.nickname);
        formDataToSend.append('gender', formData.gender);
        formDataToSend.append('birthDate', formData.birthDate);
        formDataToSend.append('birthTime', formData.birthTime);
      }
      
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4ekl27cmulmicuijgog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', referrer: '', nickname: '', gender: '', birthDate: '', birthTime: '', message: '' });
        setSelectedParts([]);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const needsDiagnosisInfo = selectedParts.includes('part1') || selectedParts.includes('part1and2');

  return (
    <section id="registration" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            お申し込み
          </h3>
          <p className="text-lg text-gray-600">
            下記フォームよりお申し込みください
          </p>
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-blue-50 p-8 lg:p-12 rounded-2xl">
          <form onSubmit={handleSubmit} data-readdy-form id="registration-form">
            <div className="space-y-6">
              {/* 参加プラン選択 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  参加プラン <span className="text-red-500">*</span>
                </label>
                <div className="grid md:grid-cols-2 gap-4">
                  {partOptions.map((option) => (
                    <div
                      key={option.id}
                      onClick={() => togglePart(option.id)}
                      className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedParts.includes(option.id)
                          ? 'border-teal-600 bg-teal-50 shadow-lg'
                          : 'border-gray-300 bg-white hover:border-teal-400'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              selectedParts.includes(option.id)
                                ? 'border-teal-600 bg-teal-600'
                                : 'border-gray-300'
                            }`}>
                              {selectedParts.includes(option.id) && (
                                <i className="ri-check-line text-white text-sm"></i>
                              )}
                            </div>
                            <h4 className="font-bold text-gray-900">{option.label}</h4>
                          </div>
                          <p className="text-2xl font-bold text-teal-600 ml-9">{option.price}</p>
                        </div>
                        {option.badge && (
                          <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">
                            {option.badge}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {selectedParts.length === 0 && (
                  <p className="text-sm text-red-500 mt-2">
                    ※ 参加プランを1つ以上選択してください
                  </p>
                )}
              </div>

              <div className="border-t border-gray-300 pt-6"></div>

              {/* 基本情報 */}
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-4">基本情報</h4>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                      placeholder="山田 太郎"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                      placeholder="example@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                      placeholder="090-1234-5678"
                    />
                  </div>

                  <div>
                    <label htmlFor="referrer" className="block text-sm font-medium text-gray-700 mb-2">
                      紹介者（任意）
                    </label>
                    <input
                      type="text"
                      id="referrer"
                      name="referrer"
                      value={formData.referrer}
                      onChange={(e) => setFormData({ ...formData, referrer: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                      placeholder="紹介者のお名前"
                    />
                  </div>
                </div>
              </div>

              {/* 第1部診断用の情報 */}
              {needsDiagnosisInfo && (
                <>
                  <div className="border-t border-gray-300 pt-6"></div>
                  <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200">
                    <div className="flex items-start gap-3 mb-4">
                      <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                      <div>
                        <h4 className="font-bold text-gray-900 mb-2">第1部の診断に必要な情報</h4>
                        <p className="text-sm text-gray-700">
                          第1部「自分と相手の取扱説明書」の診断に必要な情報をご記入ください。<br />
                          お一人一つまでの診断をいたします。
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-2">
                          ニックネーム <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="nickname"
                          name="nickname"
                          required={needsDiagnosisInfo}
                          value={formData.nickname}
                          onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                          placeholder="たろう"
                        />
                      </div>

                      <div>
                        <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                          性別 <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="gender"
                          name="gender"
                          required={needsDiagnosisInfo}
                          value={formData.gender}
                          onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white cursor-pointer"
                        >
                          <option value="">選択してください</option>
                          <option value="男性">男性</option>
                          <option value="女性">女性</option>
                          <option value="その他">その他</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 mb-2">
                          誕生日（西暦） <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          id="birthDate"
                          name="birthDate"
                          required={needsDiagnosisInfo}
                          value={formData.birthDate}
                          onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white cursor-pointer"
                        />
                      </div>

                      <div>
                        <label htmlFor="birthTime" className="block text-sm font-medium text-gray-700 mb-2">
                          出生時間（わかる場合のみ）
                        </label>
                        <input
                          type="time"
                          id="birthTime"
                          name="birthTime"
                          value={formData.birthTime}
                          onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white cursor-pointer"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          出生時間がわかる場合は、より詳しい診断が可能です
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="border-t border-gray-300 pt-6"></div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  メッセージ（任意）
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="ご質問やご要望がございましたらご記入ください（500文字以内）"
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">
                  {formData.message.length}/500文字
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || selectedParts.length === 0}
                className="w-full bg-gradient-to-r from-teal-600 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-medium hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? '送信中...' : '申し込む'}
              </button>
            </div>
          </form>

          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-xl"></i>
                <p>お申し込みありがとうございます。確認メールをお送りしました。</p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <div className="flex items-center gap-2">
                <i className="ri-error-warning-line text-xl"></i>
                <p>送信に失敗しました。もう一度お試しください。</p>
              </div>
            </div>
          )}
        </div>

        {/* 注意事項 */}
        <div className="mt-12 bg-gray-50 p-6 rounded-xl">
          <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-information-line text-teal-600"></i>
            ご注意事項
          </h4>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
              <span>定員になり次第、受付を終了させていただきます</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
              <span>お申し込み後、確認メールをお送りいたします</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
              <span>キャンセルの場合は、開催日の3日前までにご連絡ください</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-arrow-right-s-line text-teal-600 mt-0.5"></i>
              <span>第1部の診断はお一人一つまでとさせていただきます</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
